/**
 * Single owner of the admin mutation ritual (#340 item 1).
 *
 * Every workspace mutation used to hand-roll the same scaffold — a dedicated
 * busy flag, try/catch, error routing (or worse, console.error only), a
 * post-success refetch, finally-clear — spelled out 19+ times across the hook
 * and the sections, with failures landing in three different sinks. This
 * module owns all of it behind one interface:
 *
 *   const ok = await mutations.run("send-quote", "Send quote", () => api(...));
 *
 * - **Busy state** is a set of in-flight keys: `isPending(key)` drives button
 *   loading; `anyPending` is the coarse "something is happening" signal.
 *   A key that is already pending is not re-run (double-click guard) — `run`
 *   returns false.
 * - **Error sink**: a failure routes "<label> failed: <message>" into `error`
 *   (and console.error for the stack). Starting any mutation clears it.
 *   `run` never throws.
 * - **Refetch**: the configured `refetch` runs after success (and after
 *   `onSuccess`), so callers can't forget it. Opt out with `refetch: false`
 *   when a poll loop follows instead.
 */
import { useCallback, useRef, useState } from "react";
import { ApiError } from "../../../shared/api-client";

/** The one error normaliser: prefers the server's ApiError message. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export interface RunOptions {
  /** Runs after the mutation succeeds, before the refetch. */
  onSuccess?: () => void;
  /** Set false when the caller polls for the result instead of refetching. */
  refetch?: boolean;
}

export interface MutationRunner {
  /** Keys of in-flight mutations. */
  pending: ReadonlySet<string>;
  isPending: (key: string) => boolean;
  /** True while any mutation is in flight. */
  anyPending: boolean;
  /** "<label> failed: <message>" of the most recent failure, or null. */
  error: string | null;
  setError: (v: string | null) => void;
  /**
   * Run a mutation. Returns true on success, false on failure or when the
   * key is already pending. Never throws.
   */
  run: (
    key: string,
    label: string,
    fn: () => Promise<unknown>,
    opts?: RunOptions,
  ) => Promise<boolean>;
}

export function useMutationRunner(
  options: { refetch?: () => Promise<unknown> } = {},
): MutationRunner {
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  // Refs keep `run` referentially stable and make the double-fire guard
  // immune to React batching (two clicks in one tick see the same state).
  const pendingRef = useRef<Set<string>>(new Set());
  const refetchRef = useRef(options.refetch);
  refetchRef.current = options.refetch;

  const run = useCallback(
    async (
      key: string,
      label: string,
      fn: () => Promise<unknown>,
      opts: RunOptions = {},
    ): Promise<boolean> => {
      if (pendingRef.current.has(key)) return false;
      pendingRef.current.add(key);
      setPending(new Set(pendingRef.current));
      setError(null);
      try {
        await fn();
        opts.onSuccess?.();
        if (opts.refetch !== false) await refetchRef.current?.();
        return true;
      } catch (err) {
        console.error(`${label} failed:`, err);
        setError(`${label} failed: ${errorMessage(err)}`);
        return false;
      } finally {
        pendingRef.current.delete(key);
        setPending(new Set(pendingRef.current));
      }
    },
    [],
  );

  return {
    pending,
    isPending: (key: string) => pending.has(key),
    anyPending: pending.size > 0,
    error,
    setError,
    run,
  };
}
